import { create } from "zustand";
import type { 
  SeasonalityAlert, 
  UserSeasonalityPreferences,
  SeasonalityDashboardItem 
} from "../types/database";

interface SeasonalityState {
  // Metrics data
  metrics: SeasonalityDashboardItem[];
  selectedRegion: string | null;
  selectedYear: number;
  
  // Alerts
  alerts: SeasonalityAlert[];
  activeAlerts: SeasonalityAlert[];
  
  // User preferences
  preferences: UserSeasonalityPreferences | null;
  
  // UI State
  isLoading: boolean;
  chartView: 'line' | 'bar';
  showAlertPanel: boolean;
  activeTab: 'stagionalita' | 'arrivi';

  // ISTAT filters
  istatTerritorio: string | null;
  istatAnno: number;
  istatResidenza: string;
  istatTipologia: string;
  istatProvincia: string | null;
  istatComune: string | null;
  comparisonTerritory: string | null;
  comparisonTerritories: string[];
  istatAnnoCompare: number | null;

  // Actions
  setMetrics: (metrics: SeasonalityDashboardItem[]) => void;
  setSelectedRegion: (region: string | null) => void;
  setSelectedYear: (year: number) => void;
  
  setAlerts: (alerts: SeasonalityAlert[]) => void;
  dismissAlert: (id: string) => void;
  
  setPreferences: (preferences: UserSeasonalityPreferences | null) => void;
  updatePreferences: (updates: Partial<UserSeasonalityPreferences>) => void;
  
  setIsLoading: (isLoading: boolean) => void;
  setChartView: (view: 'line' | 'bar') => void;
  setShowAlertPanel: (show: boolean) => void;
  setActiveTab: (tab: 'stagionalita' | 'arrivi') => void;

  // ISTAT actions
  setIstatTerritorio: (t: string | null) => void;
  setIstatAnno: (y: number) => void;
  setIstatResidenza: (r: string) => void;
  setIstatTipologia: (t: string) => void;
  setIstatProvincia: (p: string | null) => void;
  setIstatComune: (c: string | null) => void;
  setComparisonTerritory: (t: string | null) => void;
  setComparisonTerritories: (t: string[]) => void;
  setIstatAnnoCompare: (y: number | null) => void;

  // Computed getters
  getMetricsByRegion: (region: string) => SeasonalityDashboardItem[];
  getLowSeasonPeriods: () => SeasonalityDashboardItem[];
  getUpcomingLowPeriods: () => SeasonalityDashboardItem[];
}

export const useSeasonalityStore = create<SeasonalityState>((set, get) => ({
  // Initial state
  metrics: [],
  selectedRegion: null,
  selectedYear: new Date().getFullYear(),
  alerts: [],
  activeAlerts: [],
  preferences: null,
  isLoading: false,
  chartView: 'line',
  showAlertPanel: false,
  activeTab: 'stagionalita',
  istatTerritorio: "Italia",
  istatAnno: 0,
  istatResidenza: 'mondo',
  istatTipologia: 'totale',
  istatProvincia: null,
  istatComune: null,
  comparisonTerritory: null,
  comparisonTerritories: [],
  istatAnnoCompare: null,

  // Metrics actions
  setMetrics: (metrics) => set({ metrics }),
  setSelectedRegion: (region) => set({ selectedRegion: region }),
  setSelectedYear: (year) => set({ selectedYear: year }),
  
  // Alerts actions
  setAlerts: (alerts) => set({ 
    alerts,
    activeAlerts: alerts.filter((a) => !a.is_dismissed),
  }),
  
  dismissAlert: (id) => set((state) => ({
    alerts: state.alerts.map((a) =>
      a.id === id ? { ...a, is_dismissed: true } : a
    ),
    activeAlerts: state.activeAlerts.filter((a) => a.id !== id),
  })),
  
  // Preferences actions
  setPreferences: (preferences) => set({ preferences }),
  
  updatePreferences: (updates) => set((state) => ({
    preferences: state.preferences 
      ? { ...state.preferences, ...updates }
      : null,
  })),
  
  // UI actions
  setIsLoading: (isLoading) => set({ isLoading }),
  setChartView: (view) => set({ chartView: view }),
  setShowAlertPanel: (show) => set({ showAlertPanel: show }),
  setActiveTab: (tab) => set({ activeTab: tab }),
  setIstatTerritorio: (t) => set({ istatTerritorio: t }),
  setIstatAnno: (y) => set({ istatAnno: y }),
  setIstatResidenza: (r) => set({ istatResidenza: r }),
  setIstatTipologia: (t) => set({ istatTipologia: t }),
  setIstatProvincia: (p) => set({ istatProvincia: p }),
  setIstatComune: (c) => set({ istatComune: c }),
  setComparisonTerritory: (t) => set({ comparisonTerritory: t }),
  setComparisonTerritories: (t) => set({ comparisonTerritories: t }),
  setIstatAnnoCompare: (y) => set({ istatAnnoCompare: y }),

  // Computed getters
  getMetricsByRegion: (region) => {
    const { metrics } = get();
    return metrics.filter((m) => m.region === region);
  },
  
  getLowSeasonPeriods: () => {
    const { metrics } = get();
    return metrics.filter((m) => m.is_low_season);
  },
  
  getUpcomingLowPeriods: () => {
    const { metrics } = get();
    const now = new Date();
    const threeMonthsLater = new Date();
    threeMonthsLater.setMonth(threeMonthsLater.getMonth() + 3);
    
    return metrics.filter((m) => {
      const periodStart = new Date(m.period_start);
      return m.is_low_season && periodStart >= now && periodStart <= threeMonthsLater;
    });
  },
}));

